import { useEffect, useState } from "react";
import { Trash2, Loader2, Download } from "lucide-react";
import { toast } from "sonner";
import { PROPERTIES } from "@/data/site";
import { getAllPhotos, deletePhoto, reassignPhoto, importPhotos, photoSrc } from "@/lib/api";

const VALID = PROPERTIES.map((p) => p.slug);
const nameFor = (slug) => PROPERTIES.find((p) => p.slug === slug)?.name || slug;

// Pulls the built-in site galleries into the database so every photo is manageable here
export const OwnerPhotoImport = ({ onDone }) => {
  const [busy, setBusy] = useState(false);
  const [counts, setCounts] = useState({});

  const load = () =>
    getAllPhotos()
      .then((r) => {
        const c = {};
        r.data.forEach((p) => {
          c[p.property_slug] = (c[p.property_slug] || 0) + 1;
        });
        setCounts(c);
      })
      .catch(() => setCounts({}));

  useEffect(() => {
    load();
  }, []);

  const runImport = async () => {
    setBusy(true);
    let added = 0;
    try {
      for (const p of PROPERTIES) {
        const res = await importPhotos(p.slug, p.gallery);
        added += res.data.length;
      }
      toast.success(added ? `${added} built-in photo(s) added to the library` : "Everything is already here");
      await load();
      onDone?.();
    } catch {
      toast.error("Could not import the built-in photos");
    } finally {
      setBusy(false);
    }
  };

  const missing = PROPERTIES.filter((p) => !counts[p.slug]);

  return (
    <div className="bg-white border border-[#E5E5E5] rounded-md p-5" data-testid="owner-photo-import">
      <h3 className="text-sm font-medium">Photo library</h3>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mt-4">
        {PROPERTIES.map((p) => (
          <div key={p.slug} className="border border-[#E5E5E5] rounded-md px-3 py-2.5">
            <p className="text-xs text-neutral-500">{p.name}</p>
            <p className="text-sm font-medium mt-1" data-testid={`owner-photo-count-${p.slug}`}>
              {counts[p.slug] || 0} photo(s)
            </p>
          </div>
        ))}
      </div>
      {missing.length > 0 && (
        <p className="text-xs text-neutral-500 mt-4">
          {missing.map((p) => p.name).join(", ")} still show their built-in website photos, which are not yet
          listed here. Import them once and you can then delete or reorganise any of them.
        </p>
      )}
      <button
        onClick={runImport}
        disabled={busy}
        className="mt-4 inline-flex items-center gap-2 bg-[#0F2A52] text-white px-4 py-2.5 rounded-md text-sm disabled:opacity-60"
        data-testid="owner-import-builtin"
      >
        {busy ? <Loader2 size={15} className="animate-spin" /> : <Download size={15} />}
        {busy ? "Importing…" : "Import built-in website photos"}
      </button>
    </div>
  );
};

// Any photo whose property no longer exists (old placeholder listings)
export const OwnerOrphanPhotos = ({ refreshKey, onChange }) => {
  const [orphans, setOrphans] = useState([]);
  const [busy, setBusy] = useState(false);

  const load = () =>
    getAllPhotos()
      .then((r) => setOrphans(r.data.filter((p) => !VALID.includes(p.property_slug))))
      .catch(() => setOrphans([]));

  useEffect(() => {
    load();
  }, [refreshKey]);

  const remove = async (id) => {
    try {
      await deletePhoto(id);
      setOrphans((list) => list.filter((p) => p.id !== id));
      onChange?.();
    } catch {
      toast.error("Could not delete that photo");
    }
  };

  const removeAll = async () => {
    if (!window.confirm(`Delete all ${orphans.length} leftover photo(s)? This cannot be undone.`)) return;
    setBusy(true);
    try {
      await Promise.all(orphans.map((p) => deletePhoto(p.id)));
      setOrphans([]);
      toast.success("Leftover photos removed");
      onChange?.();
    } catch {
      toast.error("Some photos could not be deleted");
    } finally {
      setBusy(false);
    }
  };

  const move = async (photo, slug) => {
    try {
      await reassignPhoto(photo.id, slug);
      setOrphans((list) => list.filter((p) => p.id !== photo.id));
      toast.success(`Moved to ${nameFor(slug)}`);
      onChange?.();
    } catch {
      toast.error("Could not move that photo");
    }
  };

  if (!orphans.length) return null;

  const bySlug = orphans.reduce((acc, p) => {
    (acc[p.property_slug] = acc[p.property_slug] || []).push(p);
    return acc;
  }, {});

  return (
    <div className="bg-white border border-amber-300 rounded-md p-5" data-testid="owner-orphan-photos">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h3 className="text-sm font-medium text-amber-900">
            Leftover photos from old listings ({orphans.length})
          </h3>
          <p className="text-xs text-neutral-600 mt-1.5 max-w-xl">
            These belong to properties that are no longer on the website. They are not shown to guests. Move
            one to a current property, or delete it.
          </p>
        </div>
        <button
          onClick={removeAll}
          disabled={busy}
          className="text-xs rounded-md px-3 py-2 border border-red-300 text-red-700 disabled:opacity-60"
          data-testid="owner-delete-all-orphans"
        >
          {busy ? "Deleting…" : "Delete all"}
        </button>
      </div>

      {Object.entries(bySlug).map(([slug, list]) => (
        <div key={slug} className="mt-5">
          <p className="text-xs uppercase tracking-wider text-neutral-500">
            {slug} · {list.length} photo(s)
          </p>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-3">
            {list.map((p) => (
              <div key={p.id} className="border border-[#E5E5E5] rounded-md overflow-hidden">
                <div className="relative">
                  <img src={photoSrc(p)} alt={p.original_filename} className="w-full h-28 object-cover" />
                  <button
                    onClick={() => remove(p.id)}
                    className="absolute top-2 right-2 bg-white/90 p-1.5 rounded-md"
                    data-testid={`owner-delete-orphan-${p.id}`}
                    aria-label="Delete photo"
                  >
                    <Trash2 size={14} className="text-red-600" />
                  </button>
                </div>
                <select
                  defaultValue=""
                  onChange={(e) => e.target.value && move(p, e.target.value)}
                  className="w-full border-t border-[#E5E5E5] px-2 py-2 text-xs bg-white"
                  data-testid={`owner-move-orphan-${p.id}`}
                >
                  <option value="">Move to…</option>
                  {PROPERTIES.map((prop) => (
                    <option key={prop.slug} value={prop.slug}>
                      {prop.name}
                    </option>
                  ))}
                </select>
              </div>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
};
